/**
 * Notification Dropdown Component
 *
 * Displays notifications in a dropdown when the notification bell is clicked.
 */

import { t } from '../../i18n/translations.js';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  formatRelativeTime,
  getNotificationIcon,
  getPriorityClass,
} from '../../services/notifications.js';

// Component state
const state = {
  isOpen: false,
  notifications: [],
  unreadCount: 0,
  hasCritical: false,
  loading: false,
  error: null,
  pollInterval: null,
};

// DOM references
let dropdownEl = null;
let badgeEl = null;
let buttonEl = null;

/**
 * Initialize the notification dropdown
 */
export function init() {
  buttonEl = document.getElementById('notificationBtn');
  badgeEl = document.getElementById('notificationBadge');

  if (!buttonEl) {
    console.warn('Notification button not found');
    return;
  }

  // Create dropdown container
  createDropdown();

  // Attach click handler
  buttonEl.addEventListener('click', toggleDropdown);

  // Close on outside click
  document.addEventListener('click', handleOutsideClick);

  // Fetch initial count
  fetchUnreadCount();

  // Poll for updates every 30 seconds
  state.pollInterval = setInterval(fetchUnreadCount, 30000);
}

/**
 * Create the dropdown element
 */
function createDropdown() {
  dropdownEl = document.createElement('div');
  dropdownEl.className = 'notification-dropdown';
  dropdownEl.innerHTML = `
    <div class="notification-dropdown__header">
      <h3>${t('notifications.title') || 'Notifications'}</h3>
      <button class="notification-dropdown__mark-all" id="markAllReadBtn">
        ${t('notifications.markAllRead') || 'Mark all read'}
      </button>
    </div>
    <div class="notification-dropdown__content" id="notificationList">
      <!-- Notifications loaded here -->
    </div>
    <div class="notification-dropdown__footer">
      <a href="#/settings/notifications">${t('notifications.viewSettings') || 'Notification Settings'}</a>
    </div>
  `;

  // Insert after the button
  buttonEl.parentElement.style.position = 'relative';
  buttonEl.parentElement.appendChild(dropdownEl);

  // Attach mark all read handler
  const markAllBtn = dropdownEl.querySelector('#markAllReadBtn');
  markAllBtn.addEventListener('click', handleMarkAllRead);
}

/**
 * Toggle dropdown open/closed
 */
function toggleDropdown(e) {
  e.stopPropagation();
  state.isOpen = !state.isOpen;

  if (state.isOpen) {
    dropdownEl.classList.add('show');
    fetchNotifications();
  } else {
    dropdownEl.classList.remove('show');
  }
}

/**
 * Handle click outside dropdown
 */
function handleOutsideClick(e) {
  if (state.isOpen && !dropdownEl.contains(e.target) && e.target !== buttonEl) {
    state.isOpen = false;
    dropdownEl.classList.remove('show');
  }
}

/**
 * Fetch unread count for badge
 */
async function fetchUnreadCount() {
  try {
    const result = await getUnreadCount();
    state.unreadCount = result.count;
    state.hasCritical = result.hasCritical;
    updateBadge();
  } catch (error) {
    console.error('Failed to fetch unread count:', error);
  }
}

/**
 * Update the badge display
 */
function updateBadge() {
  if (!badgeEl) return;

  if (state.unreadCount > 0) {
    badgeEl.textContent = state.unreadCount > 99 ? '99+' : state.unreadCount;
    badgeEl.style.display = 'flex';

    // Add critical styling if needed
    if (state.hasCritical) {
      badgeEl.classList.add('notification-badge--critical');
    } else {
      badgeEl.classList.remove('notification-badge--critical');
    }
  } else {
    badgeEl.style.display = 'none';
  }
}

/**
 * Fetch notifications list
 */
async function fetchNotifications() {
  const listEl = dropdownEl.querySelector('#notificationList');
  state.loading = true;
  listEl.innerHTML = `
    <div class="notification-dropdown__loading">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const result = await getNotifications({ pageSize: 10 });
    state.notifications = result.notifications || [];
    state.unreadCount = result.unreadCount;
    state.error = null;
    updateBadge();
    renderNotifications();
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
    state.error = error.message;
    listEl.innerHTML = `
      <div class="notification-dropdown__empty">
        <p>${t('notifications.fetchError') || 'Failed to load notifications'}</p>
      </div>
    `;
  } finally {
    state.loading = false;
  }
}

/**
 * Render notifications list
 */
function renderNotifications() {
  const listEl = dropdownEl.querySelector('#notificationList');

  if (state.notifications.length === 0) {
    listEl.innerHTML = `
      <div class="notification-dropdown__empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="48" height="48">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        <p>${t('notifications.noNotifications') || 'No notifications'}</p>
      </div>
    `;
    return;
  }

  listEl.innerHTML = state.notifications
    .map((notification) => renderNotificationItem(notification))
    .join('');

  // Attach click handlers
  listEl.querySelectorAll('.notification-item').forEach((item) => {
    item.addEventListener('click', () => handleNotificationClick(item.dataset.id));
  });
}

/**
 * Render a single notification item
 */
function renderNotificationItem(notification) {
  const priorityClass = getPriorityClass(notification.priority);
  const icon = getNotificationIcon(notification.notificationType);
  const time = formatRelativeTime(notification.createdAt);

  return `
    <div class="notification-item ${!notification.read ? 'unread' : ''} ${priorityClass}"
         data-id="${notification.id}">
      <div class="notification-item__icon">
        ${icon}
      </div>
      <div class="notification-item__content">
        <div class="notification-item__title">${notification.title}</div>
        <div class="notification-item__message">${notification.message}</div>
        <div class="notification-item__time">${time}</div>
      </div>
      ${!notification.read ? '<span class="notification-item__dot"></span>' : ''}
    </div>
  `;
}

/**
 * Handle notification click
 */
async function handleNotificationClick(notificationId) {
  const notification = state.notifications.find((n) => n.id === notificationId);
  if (!notification) return;

  // Mark as read if unread
  if (!notification.read) {
    try {
      await markAsRead(notificationId);
      notification.read = true;
      state.unreadCount = Math.max(0, state.unreadCount - 1);
      updateBadge();
      renderNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }

  // Navigate if action URL exists
  if (notification.actionUrl) {
    window.location.hash = notification.actionUrl.replace('#', '');
    state.isOpen = false;
    dropdownEl.classList.remove('show');
  }
}

/**
 * Handle mark all as read
 */
async function handleMarkAllRead(e) {
  e.stopPropagation();

  try {
    await markAllAsRead();
    state.notifications.forEach((n) => (n.read = true));
    state.unreadCount = 0;
    updateBadge();
    renderNotifications();
  } catch (error) {
    console.error('Failed to mark all as read:', error);
  }
}

/**
 * Cleanup on component destroy
 */
export function destroy() {
  if (state.pollInterval) {
    clearInterval(state.pollInterval);
  }
  if (buttonEl) {
    buttonEl.removeEventListener('click', toggleDropdown);
  }
  document.removeEventListener('click', handleOutsideClick);
  if (dropdownEl) {
    dropdownEl.remove();
  }
}

/**
 * Inject notification dropdown styles
 */
export function injectStyles() {
  if (document.getElementById('notification-dropdown-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'notification-dropdown-styles';
  styles.textContent = `
    /* Notification Dropdown */
    .notification-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 380px;
      max-height: 480px;
      background: var(--bg-secondary, #ffffff);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: var(--radius-lg, 1rem);
      box-shadow: var(--shadow-lg, 0 10px 25px rgba(0, 0, 0, 0.15));
      z-index: var(--z-dropdown, 100);
      display: none;
      flex-direction: column;
      overflow: hidden;
    }

    .notification-dropdown.show {
      display: flex;
    }

    .notification-dropdown__header {
      padding: 1rem;
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .notification-dropdown__header h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
    }

    .notification-dropdown__mark-all {
      background: none;
      border: none;
      color: var(--primary, #2563eb);
      font-size: 0.875rem;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: var(--radius-sm, 0.25rem);
    }

    .notification-dropdown__mark-all:hover {
      background: var(--primary-light, #dbeafe);
    }

    .notification-dropdown__content {
      flex: 1;
      overflow-y: auto;
      max-height: 360px;
    }

    .notification-dropdown__footer {
      padding: 0.75rem 1rem;
      border-top: 1px solid var(--border-color, #e5e7eb);
      text-align: center;
    }

    .notification-dropdown__footer a {
      color: var(--text-secondary, #6b7280);
      font-size: 0.875rem;
      text-decoration: none;
    }

    .notification-dropdown__footer a:hover {
      color: var(--primary, #2563eb);
    }

    .notification-dropdown__loading,
    .notification-dropdown__empty {
      padding: 2rem;
      text-align: center;
      color: var(--text-secondary, #6b7280);
    }

    .notification-dropdown__empty svg {
      margin-bottom: 1rem;
      opacity: 0.5;
    }

    .notification-dropdown__loading .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--border-color, #e5e7eb);
      border-top-color: var(--primary, #2563eb);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Notification Item */
    .notification-item {
      padding: 1rem;
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      gap: 0.75rem;
      cursor: pointer;
      transition: background var(--transition-fast, 0.15s);
      position: relative;
    }

    .notification-item:hover {
      background: var(--gray-50, #f9fafb);
    }

    .notification-item:last-child {
      border-bottom: none;
    }

    .notification-item.unread {
      background: var(--primary-light, #eff6ff);
    }

    .notification-item.unread:hover {
      background: var(--primary-lighter, #dbeafe);
    }

    .notification-item--critical {
      border-left: 3px solid var(--error, #ef4444);
    }

    .notification-item--high {
      border-left: 3px solid var(--warning, #f59e0b);
    }

    .notification-item--medium {
      border-left: 3px solid var(--info, #3b82f6);
    }

    .notification-item--low {
      border-left: 3px solid var(--gray-300, #d1d5db);
    }

    .notification-item__icon {
      width: 36px;
      height: 36px;
      border-radius: var(--radius, 0.5rem);
      background: var(--gray-100, #f3f4f6);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--text-secondary, #6b7280);
    }

    .notification-item--critical .notification-item__icon {
      background: var(--error-light, #fee2e2);
      color: var(--error, #ef4444);
    }

    .notification-item--high .notification-item__icon {
      background: var(--warning-light, #fef3c7);
      color: var(--warning, #f59e0b);
    }

    .notification-item__content {
      flex: 1;
      min-width: 0;
    }

    .notification-item__title {
      font-weight: 600;
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
      color: var(--text-primary, #111827);
    }

    .notification-item__message {
      font-size: 0.8125rem;
      color: var(--text-secondary, #6b7280);
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .notification-item__time {
      font-size: 0.75rem;
      color: var(--text-tertiary, #9ca3af);
      margin-top: 0.25rem;
    }

    .notification-item__dot {
      position: absolute;
      top: 1rem;
      right: 1rem;
      width: 8px;
      height: 8px;
      background: var(--primary, #2563eb);
      border-radius: 50%;
    }

    /* Badge critical state */
    .notification-badge--critical {
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }

    /* RTL Support */
    [dir="rtl"] .notification-dropdown {
      right: auto;
      left: 0;
    }

    [dir="rtl"] .notification-item--critical,
    [dir="rtl"] .notification-item--high,
    [dir="rtl"] .notification-item--medium,
    [dir="rtl"] .notification-item--low {
      border-left: none;
      border-right: 3px solid;
    }

    [dir="rtl"] .notification-item--critical { border-right-color: var(--error, #ef4444); }
    [dir="rtl"] .notification-item--high { border-right-color: var(--warning, #f59e0b); }
    [dir="rtl"] .notification-item--medium { border-right-color: var(--info, #3b82f6); }
    [dir="rtl"] .notification-item--low { border-right-color: var(--gray-300, #d1d5db); }

    [dir="rtl"] .notification-item__dot {
      right: auto;
      left: 1rem;
    }

    /* Responsive */
    @media (max-width: 480px) {
      .notification-dropdown {
        position: fixed;
        top: 60px;
        right: 0.5rem;
        left: 0.5rem;
        width: auto;
        max-height: calc(100vh - 80px);
      }
    }
  `;

  document.head.appendChild(styles);
}

export default {
  init,
  destroy,
  injectStyles,
};
